import React from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Popover,
  PopoverButton,
  PopoverPanel
} from '@headlessui/react';
import clsx from 'clsx';

import { formatModifiedAt } from './picker-ui';

/**
 * The search box across the top of a tab's list, with its filter chips on a
 * row beneath so they never narrow the box.
 */
export function FilterBar(props: {
  query: string;
  onQueryChange: (query: string) => void;
  placeholder: string;
  ariaLabel: string;
  children?: React.ReactNode;
  onClear?: () => void;
}) {
  return (
    <div className="space-y-2 border-b border-neutral-100 px-3 py-2 dark:border-neutral-700">
      <div className="relative">
        <MagnifyingGlassIcon
          className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-neutral-400 dark:text-neutral-500"
          aria-hidden="true"
        />
        <input
          type="text"
          value={props.query}
          onChange={(e) => props.onQueryChange(e.currentTarget.value)}
          placeholder={props.placeholder}
          aria-label={props.ariaLabel}
          className={clsx(
            'w-full rounded-md border border-neutral-950/10 bg-white py-1 pl-8 pr-2 text-sm/6 text-neutral-950 shadow-sm',
            'placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20',
            'dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-neutral-500'
          )}
        />
      </div>
      {(props.children || props.onClear) && (
        <div className="flex flex-wrap items-center gap-2">
          {props.children}
          {props.onClear && (
            <button
              type="button"
              onClick={props.onClear}
              className="text-xs font-medium text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export const FILTER_CHIP_CLASS = clsx(
  'inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-2.5 py-1 dark:border-neutral-700 dark:bg-neutral-800',
  'text-xs font-medium text-neutral-700 shadow-sm dark:text-neutral-300',
  'hover:border-neutral-300 dark:hover:border-white/20',
  'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500',
  'data-[active]:border-primary-300 data-[active]:bg-primary-50 data-[active]:text-primary-900',
  'dark:data-[active]:border-primary-500/30 dark:data-[active]:bg-primary-500/10 dark:data-[active]:text-primary-200'
);

export const FILTER_PANEL_CLASS = clsx(
  'z-30 mt-1 w-60 rounded-xl border border-neutral-200 bg-white p-1.5 shadow-lg ring-1 ring-black/5',
  'dark:border-neutral-700 dark:bg-neutral-800 dark:ring-white/10 focus:outline-none'
);

const FILTER_OPTION_CLASS = clsx(
  'flex w-full cursor-default items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm text-neutral-700 dark:text-neutral-300',
  'data-[focus]:bg-neutral-50 dark:data-[focus]:bg-white/5',
  'data-[selected]:bg-primary-50 data-[selected]:font-medium data-[selected]:text-primary-900',
  'dark:data-[selected]:bg-primary-500/10 dark:data-[selected]:text-primary-200'
);

/** A chip's text, which is also its accessible name: "Status", or "Status: Active" once set. */
export function ChipLabel(props: { label: string; value?: string }) {
  return (
    <>
      <span className="max-w-48 truncate">
        {props.value ? `${props.label}: ${props.value}` : props.label}
      </span>
      <ChevronDownIcon
        className="size-3.5 text-neutral-400 dark:text-neutral-500"
        aria-hidden="true"
      />
    </>
  );
}

/** A filter chip that picks any number of options; none picked filters nothing. */
export function MultiSelectChip<T extends string>(props: {
  label: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  values: T[];
  onChange: (values: T[]) => void;
}) {
  const { label, options, values, onChange } = props;
  const picked = options.filter((o) => values.includes(o.value));
  const summary =
    picked.length === 0
      ? undefined
      : picked.length === 1
        ? picked[0].label
        : `${picked.length} selected`;
  return (
    <Listbox value={values} onChange={onChange} multiple>
      <ListboxButton className={FILTER_CHIP_CLASS} data-active={summary ? '' : undefined}>
        <ChipLabel label={label} value={summary} />
      </ListboxButton>
      <ListboxOptions anchor="bottom start" className={FILTER_PANEL_CLASS}>
        {options.map((option) => (
          <ListboxOption key={option.value} value={option.value} className={FILTER_OPTION_CLASS}>
            {({ selected }) => (
              <>
                <span>{option.label}</span>
                {selected && (
                  <CheckIcon className="size-4 text-primary-600 dark:text-primary-400" />
                )}
              </>
            )}
          </ListboxOption>
        ))}
      </ListboxOptions>
    </Listbox>
  );
}

/** A date window: a named preset, or a custom range of inclusive YYYY-MM-DD days. */
export type DateWindow = { preset: string; from?: string; to?: string };

export type DatePreset = {
  value: string;
  label: string;
  /** The window's days, worked out from today when the preset is picked. */
  range: () => { from?: string; to?: string };
};

export const ANY_DATE: DateWindow = { preset: 'any' };

/** Whether the window narrows anything: a custom range with neither day set does not. */
export const windowSet = (window: DateWindow): boolean =>
  window.preset !== 'any' && (window.preset !== 'custom' || !!(window.from || window.to));

/** The YYYY-MM-DD `days` from today in the user's own calendar; negative is past. */
export function dayFromToday(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function windowSummary(window: DateWindow, presets: ReadonlyArray<DatePreset>) {
  if (!windowSet(window)) return undefined;
  if (window.preset !== 'custom') return presets.find((p) => p.value === window.preset)?.label;
  const from = window.from ? formatModifiedAt(window.from) : undefined;
  const to = window.to ? formatModifiedAt(window.to) : undefined;
  if (from && to) return `${from} – ${to}`;
  return from ? `Since ${from}` : `Through ${to}`;
}

/** A filter chip that sets a date window from presets or a custom range. */
export function DateWindowChip(props: {
  label: string;
  presets: ReadonlyArray<DatePreset>;
  value: DateWindow;
  onChange: (value: DateWindow) => void;
}) {
  const { label, presets, value, onChange } = props;
  const options = [
    { value: 'any', label: 'Any time' },
    ...presets.map((p) => ({ value: p.value, label: p.label })),
    { value: 'custom', label: 'Custom range' }
  ];
  const summary = windowSummary(value, presets);
  const choose = (preset: string, close: () => void) => {
    if (preset === 'custom') {
      onChange({ preset, from: value.from, to: value.to });
      return;
    }
    const range = presets.find((p) => p.value === preset)?.range() ?? {};
    onChange({ preset, ...range });
    close();
  };
  const dateInputClass = clsx(
    'rounded-md border border-neutral-300 px-2 py-1 text-xs text-neutral-900',
    'dark:border-neutral-600 dark:bg-white/5 dark:text-white dark:[color-scheme:dark]',
    'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
  );
  return (
    <Popover className="relative">
      {({ close }) => (
        <>
          <PopoverButton className={FILTER_CHIP_CLASS} data-active={summary ? '' : undefined}>
            <ChipLabel label={label} value={summary} />
          </PopoverButton>
          <PopoverPanel anchor="bottom start" className={FILTER_PANEL_CLASS}>
            {options.map((option) => {
              const selected = value.preset === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => choose(option.value, close)}
                  aria-pressed={selected}
                  data-selected={selected ? '' : undefined}
                  className={FILTER_OPTION_CLASS}
                >
                  <span>{option.label}</span>
                  {selected && (
                    <CheckIcon className="size-4 text-primary-600 dark:text-primary-400" />
                  )}
                </button>
              );
            })}
            {value.preset === 'custom' && (
              <div className="mt-1.5 grid grid-cols-2 gap-2 border-t border-neutral-100 px-1 pb-1 pt-2 dark:border-neutral-700">
                <label className="flex flex-col gap-1 text-xs text-neutral-600 dark:text-neutral-400">
                  From
                  <input
                    type="date"
                    value={value.from ?? ''}
                    max={value.to || undefined}
                    onChange={(e) =>
                      onChange({ ...value, from: e.currentTarget.value || undefined })
                    }
                    className={dateInputClass}
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-neutral-600 dark:text-neutral-400">
                  To
                  <input
                    type="date"
                    value={value.to ?? ''}
                    min={value.from || undefined}
                    onChange={(e) => onChange({ ...value, to: e.currentTarget.value || undefined })}
                    className={dateInputClass}
                  />
                </label>
              </div>
            )}
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
}
